import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, X, Save, ShoppingCart } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const CreateOrder = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'India'
  });
  const [paymentMethod, setPaymentMethod] = useState('cod');
  
  // New customer modal state
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  
  // Guest checkout state
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [customersData, productsData] = await Promise.all([
        apiCall('/api/customers/'),
        apiCall('/api/listings/?listing_type=Products&status=active')
      ]);
      setCustomers(customersData || []);
      setProducts(productsData || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    setShippingAddress({
      name: customer.name,
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'India'
    });
  };
  
  const handleCreateNewCustomer = async () => {
    if (!newCustomerData.name || !newCustomerData.email || !newCustomerData.password) {
      toast({
        title: 'Error',
        description: 'Please fill all customer fields',
        variant: 'destructive'
      });
      return;
    }
    
    setCreatingCustomer(true);
    try {
      const newCustomer = await apiCall('/api/customers/', {
        method: 'POST',
        body: JSON.stringify(newCustomerData)
      });
      
      // Add to customers list and select it
      setCustomers([...customers, newCustomer]);
      setSelectedCustomer(newCustomer);
      setShippingAddress({
        name: newCustomer.name,
        address: '',
        city: '',
        state: '',
        zip: '',
        country: 'India'
      });
      
      // Reset form and close modal
      setNewCustomerData({ name: '', email: '', password: '' });
      setShowNewCustomerModal(false);
      
      toast({
        title: 'Success',
        description: 'Customer created successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create customer',
        variant: 'destructive'
      });
    } finally {
      setCreatingCustomer(false);
    }
  };
  
  const addProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    const existing = orderItems.find(item => item.id === productId);
    if (existing) {
      toast({
        title: 'Already Added',
        description: 'This product is already in the order',
        variant: 'destructive'
      });
      return;
    }
    
    setOrderItems([...orderItems, {
      id: product.id,
      name: product.title,
      sku: product.data?.sku || 'N/A',
      quantity: 1,
      price: product.data?.price || 0,
      image: product.media?.[0] || null
    }]);
  };
  
  const updateQuantity = (itemId, quantity) => {
    setOrderItems(orderItems.map(item =>
      item.id === itemId ? { ...item, quantity: parseInt(quantity) || 1 } : item
    ));
  };
  
  const removeItem = (itemId) => {
    setOrderItems(orderItems.filter(item => item.id !== itemId));
  };
  
  const calculatePricing = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tax = subtotal * 0.18; // 18% GST
    const shipping = subtotal > 500 ? 0 : 50;
    const total = subtotal + tax + shipping;
    
    return { subtotal, tax, shipping, discount: 0, total };
  };
  
  const handleSubmit = async () => {
    if (!selectedCustomer) {
      toast({
        title: 'Error',
        description: 'Please select a customer',
        variant: 'destructive'
      });
      return;
    }
    
    if (orderItems.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one product',
        variant: 'destructive'
      });
      return;
    }
    
    if (!shippingAddress.address || !shippingAddress.city) {
      toast({
        title: 'Error',
        description: 'Please fill in shipping address',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSaving(true);
      const pricing = calculatePricing();
      
      const orderData = {
        customer: {
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          phone: selectedCustomer.phone || 'N/A'
        },
        vendor: {
          name: 'Admin Created',
          email: 'admin@marketplace.com',
          phone: 'N/A'
        },
        items: orderItems,
        shipping_address: shippingAddress,
        billing_address: shippingAddress,
        payment: {
          method: paymentMethod,
          card_type: null,
          last_four_digits: null,
          transaction_id: `TXN-${Date.now()}`
        },
        pricing: pricing,
        order_type: 'product'
      };
      
      await apiCall('/api/orders/', {
        method: 'POST',
        body: JSON.stringify(orderData)
      });
      
      toast({
        title: 'Success',
        description: 'Order created successfully'
      });
      
      navigate('/orders');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const pricing = calculatePricing();
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Button>
        </div>
        
        <div className="flex items-center gap-3">
          <ShoppingCart className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create New Order</h1>
            <p className="text-gray-600">Create an order manually for a customer</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="guestOrder"
                    checked={isGuestCheckout}
                    onChange={(e) => setIsGuestCheckout(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="guestOrder" className="cursor-pointer">
                    Guest Order (No account required)
                  </Label>
                </div>
                
                {!isGuestCheckout ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>Select Customer *</Label>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowNewCustomerModal(true)}
                        type="button"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add New
                      </Button>
                    </div>
                    <Select 
                      onValueChange={handleCustomerSelect}
                      value={selectedCustomer?.id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(customer => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} ({customer.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 border rounded-lg bg-blue-50">
                    <h4 className="font-semibold text-sm">Guest Information</h4>
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={guestInfo.name}
                        onChange={(e) => setGuestInfo({...guestInfo, name: e.target.value})}
                        placeholder="Enter guest name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={guestInfo.email}
                        onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
                        placeholder="guest@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone *</Label>
                      <Input
                        type="tel"
                        value={guestInfo.phone}
                        onChange={(e) => setGuestInfo({...guestInfo, phone: e.target.value})}
                        placeholder="+91 99999 99999"
                      />
                    </div>
                  </div>
                )}
                
                {selectedCustomer && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium">{selectedCustomer.name}</p>
                    <p className="text-sm text-gray-600">{selectedCustomer.email}</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Order Items</CardTitle>
                  <Select onValueChange={addProduct}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Add product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.title} - ₹{product.data?.price || 0}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {orderItems.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No items added yet</p>
                ) : (
                  <div className="space-y-3">
                    {orderItems.map(item => (
                      <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                        </div>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.id, e.target.value)}
                          className="w-20"
                        />
                        <span className="font-semibold w-24 text-right">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(item.id)}
                        >
                          <X className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Shipping Address</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={shippingAddress.name}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address *</Label>
                  <Textarea
                    value={shippingAddress.address}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Input
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <Input
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ZIP Code *</Label>
                    <Input
                      value={shippingAddress.zip}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, zip: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={shippingAddress.country}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Summary */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold">₹{pricing.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax (18%):</span>
                    <span className="font-semibold">₹{pricing.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping:</span>
                    <span className="font-semibold">₹{pricing.shipping.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between text-lg">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold">₹{pricing.total.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cod">Cash on Delivery</SelectItem>
                      <SelectItem value="online">Online Payment</SelectItem>
                      <SelectItem value="card">Credit/Debit Card</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  className="w-full" 
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Creating Order...' : 'Create Order'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* New Customer Modal */}
        {showNewCustomerModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Create New Customer</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowNewCustomerModal(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Customer Name *</Label>
                  <Input
                    value={newCustomerData.name}
                    onChange={(e) => setNewCustomerData({...newCustomerData, name: e.target.value})}
                    placeholder="Enter customer name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newCustomerData.email}
                    onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})}
                    placeholder="customer@example.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newCustomerData.password}
                    onChange={(e) => setNewCustomerData({...newCustomerData, password: e.target.value})}
                    placeholder="Enter password"
                  />
                  <p className="text-xs text-gray-500">Customer will use this to login</p>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowNewCustomerModal(false)}
                    disabled={creatingCustomer}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateNewCustomer}
                    disabled={creatingCustomer}
                    className="flex-1"
                  >
                    {creatingCustomer ? 'Creating...' : 'Create Customer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CreateOrder;
